import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { MOTIVO_LIBERAR_EN, hoyYmd, paredEn, fechaHoraEn } from "@/lib/chofer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { abrirArchivo } from "@/components/chofer/AgregarArchivo";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): diálogos de la carga del chofer, en inglés.
// «Update ETA»: la hora de llegada al receiver, en la hora y zona del receiver. Cada ETA queda en el historial; la cita no cambia.
export function EtaDialog({ cargaId, vigente, zonaLabel, onClose, onCarga }) {
  const [fecha, setFecha] = useState(vigente?.eta_fecha || hoyYmd());
  const [hora, setHora] = useState(vigente?.eta_hora || "");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!fecha || !hora) return toast.error(t("Enter the ETA date and time"));
    setSaving(true);
    try {
      const { data } = await api.post(`/chofer/cargas/${cargaId}/eta`, { eta_fecha: fecha, eta_hora: hora });
      toast.success(t("ETA updated"));
      onCarga(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{t("Update ETA")}</DialogTitle></DialogHeader>
        <p className="text-[14px] text-ink-2">{t("Delivery ETA · receiver local time ({zonaLabel})", { zonaLabel })}</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="eyebrow">{t("Date")}</Label><Input data-testid="eta-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1 h-12" /></div>
          <div><Label className="eyebrow">{t("Time")}</Label><Input data-testid="eta-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} className="mt-1 h-12" /></div>
        </div>
        {vigente && <p className="text-[13px] text-ink-3">{t("Current ETA: {v0}", { v0: paredEn(vigente.eta_fecha, vigente.eta_hora, vigente.zona_horaria) })}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-12" onClick={onClose}>{t("Cancel")}</Button>
          <Button data-testid="eta-guardar" className="h-12" onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Save ETA")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// «I can't finish this trip»: libera solo las etapas pendientes de este chofer.
export function LiberarDialog({ cargaId, onClose, onDone }) {
  const [ubic, setUbic] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [texto, setTexto] = useState("");
  const [ubicId, setUbicId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/chofer/ubicaciones").then((r) => setUbic(r.data)).catch(() => {}); }, []);
  const confirmar = async () => {
    if (!motivo) return toast.error(t("Choose the reason"));
    if (!ubicId) return toast.error(t("Choose where it was left"));
    setSaving(true);
    try {
      await api.post(`/chofer/cargas/${cargaId}/liberar`, { motivo, texto, ubicacion_id: ubicId });
      toast.success(t("Your pending steps were released"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle className="text-danger">{t("I can’t finish this trip")}</DialogTitle></DialogHeader>
        <p className="text-[14px] text-ink-2">{t("The steps you already finished stay in your name. Your pending steps go back to dispatch.")}</p>
        <div className="space-y-3">
          <div>
            <Label className="eyebrow">{t("Reason")}</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger data-testid="liberar-motivo" className="h-12 bg-white mt-1"><SelectValue placeholder={t("Choose a reason")} /></SelectTrigger>
              <SelectContent className="bg-white">
                {Object.entries(MOTIVO_LIBERAR_EN).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea data-testid="liberar-texto" placeholder={t("Details (optional)")} value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} />
          <div>
            <Label className="eyebrow">{t("Where was it left?")}</Label>
            <Select value={ubicId} onValueChange={setUbicId}>
              <SelectTrigger data-testid="liberar-ubicacion" className="h-12 bg-white mt-1"><SelectValue placeholder={t("Choose a location")} /></SelectTrigger>
              <SelectContent className="bg-white">
                {ubic.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-12" onClick={onClose}>{t("Cancel")}</Button>
          <Button data-testid="liberar-confirmar" className="h-12 bg-danger hover:bg-[#A92825] text-white" onClick={confirmar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Release my steps")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// «Files (N)»: los PDF y fotos que subió el chofer a esta carga, con su etapa y la hora de subida.
export function ArchivosDialog({ archivos, nombreEtapa, zonaDe, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{t("Uploaded files ({length})", { length: archivos.length })}</DialogTitle></DialogHeader>
        {archivos.length === 0 ? (
          <p className="text-sm text-ink-3 py-4" data-testid="archivos-vacio">{t("You have not uploaded any files to this load yet.")}</p>
        ) : (
          <ul className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="archivos-lista">
            {archivos.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => abrirArchivo(a.file_id)} className="text-left w-full" data-testid={`archivos-item-${a.id}`}>
                  <span className="block text-[15.5px] text-ink">{nombreEtapa(a.etapa)}: {a.nombre}</span>
                  <span className="block text-[12.5px] text-ink-2">{t("Uploaded {v0}", { v0: fechaHoraEn(a.subido_at, zonaDe(a.etapa)) })}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter><Button variant="outline" className="h-12" onClick={onClose}>{t("Close")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
